import React, { useRef, useState } from "react";
import BaseField from "./BaseField";

export default function SignatureField({
  field,
  value,
  setValue,
  renderInlineChildren,
  openViewer,
  previewMode = false,
}) {
  const required = !!field.required;
  const label = field.label || "Signature";
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  const handleMouseDown = (e) => {
    setIsDrawing(true);
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const handleMouseUp = () => {
    setIsDrawing(false);
  };

  const handleMouseMove = (e) => {
    if (!isDrawing || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#000";
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const clearSignature = () => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setValue(null);
    }
  };

  const saveSignature = () => {
    if (canvasRef.current) {
      const signatureImage = canvasRef.current.toDataURL();
      setValue({
        ...(value || {}),
        signature: signatureImage,
      });
    }
  };

  return (
    <BaseField
      field={field}
      value={value}
      setValue={setValue}
      openViewer={openViewer}
      renderInlineChildren={renderInlineChildren}
      previewMode={previewMode}
    >
      <h3 className="text-gray-800 dark:text-gray-100 text-lg font-bold mb-3 text-center">
        {label} {required && <span className="text-orange-500">*</span>}
      </h3>

      {field.config?.requireTypedName && (
        <input
          type="text"
          value={value?.name ?? ""}
          onChange={(e) =>
            setValue({
              ...(value || {}),
              name: e.target.value,
            })
          }
          placeholder="Full name"
          className="w-full border-2 border-orange-500 rounded-lg p-3 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 mb-3 focus:outline-none focus:ring-2 focus:ring-orange-400"
        />
      )}

      {value?.signature ? (
        <div className="flex flex-col gap-2">
          <img
            src={value.signature}
            alt="signature"
            className="border-2 border-gray-300 rounded-lg bg-white dark:bg-gray-700 p-2"
          />
          <button
            type="button"
            onClick={clearSignature}
            className="px-4 py-2 rounded-lg border-2 border-orange-500 bg-white dark:bg-gray-700 text-orange-600 dark:text-orange-400 font-semibold hover:bg-orange-50 dark:hover:bg-gray-600"
          >
            ✏️ Redraw
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <canvas
            ref={canvasRef}
            width={300}
            height={150}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
            className="border-2 border-orange-500 rounded-lg bg-white dark:bg-gray-700 cursor-crosshair"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={saveSignature}
              className="flex-1 px-4 py-2 rounded-lg border-2 border-orange-500 bg-orange-500 text-white font-semibold hover:bg-orange-600"
            >
              ✓ Save
            </button>
            <button
              type="button"
              onClick={clearSignature}
              className="flex-1 px-4 py-2 rounded-lg border-2 border-gray-400 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 font-semibold hover:bg-gray-100 dark:hover:bg-gray-600"
            >
              ✕ Clear
            </button>
          </div>
        </div>
      )}
    </BaseField>
  );
}
